import { useRef } from 'react';
import { IonIcon, IonSelect, IonSelectOption } from '@ionic/react';
import { chevronBack, chevronForward, calendarOutline, trophyOutline } from 'ionicons/icons';
import { SelectField } from '../../../../components/ui/SelectField';
import { CompetitionName } from '../../components/CompetitionName';
import { formatCompetitionLabel } from '../../components/competitionLabel';
import type { MatchWeekSelection } from '../hooks/useMatchWeekSelection';

interface MatchWeekSelectorProps {
  selection: MatchWeekSelection;
}

/** Season and competition selectors, plus the match week navigator once matches are loaded. */
export const MatchWeekSelector = ({ selection }: MatchWeekSelectorProps) => {
  const {
    seasons,
    seasonsLoading,
    selectedSeasonId,
    seasonCompetitions,
    seasonCompetitionsLoading,
    selectedSeasonCompetitionId,
    matchesQuery,
    selectSeason,
    selectSeasonCompetition,
    goToPreviousMatchWeek,
    goToNextMatchWeek,
  } = selection;
  const matchWeekMatches = matchesQuery.data?.getMatches;

  const seasonSelectRef = useRef<HTMLIonSelectElement>(null);
  const competitionSelectRef = useRef<HTMLIonSelectElement>(null);

  const selectedSeasonName = seasons.find((season) => season.id === selectedSeasonId)?.displayName;
  const selectedCompetition = seasonCompetitions.find(
    (seasonCompetition) => seasonCompetition.id === selectedSeasonCompetitionId
  )?.competition;

  return (
    <>
      <div className="mx-4 mt-3 grid grid-cols-2 gap-3">
        <div className="relative">
          <SelectField
            label="Temporada"
            icon={calendarOutline}
            value={selectedSeasonName ?? '—'}
            disabled={seasonsLoading}
            aria-label="Seleccionar temporada"
            onClick={() => seasonSelectRef.current?.open()}
          />
          <IonSelect
            ref={seasonSelectRef}
            aria-label="Temporada"
            interface="popover"
            value={selectedSeasonId}
            disabled={seasonsLoading}
            onIonChange={(event) => selectSeason(event.detail.value as string | undefined)}
            className="pointer-events-none absolute inset-0 opacity-0"
          >
            {seasons.map((season) => (
              <IonSelectOption key={season.id} value={season.id}>
                {season.displayName}
              </IonSelectOption>
            ))}
          </IonSelect>
        </div>

        <div className="relative">
          <SelectField
            label="Competición"
            icon={trophyOutline}
            value={
              selectedCompetition ? (
                <CompetitionName
                  displayName={selectedCompetition.displayName}
                  autonomousCommunity={selectedCompetition.autonomousCommunity}
                />
              ) : (
                '—'
              )
            }
            disabled={seasonCompetitionsLoading}
            aria-label="Seleccionar competición"
            onClick={() => competitionSelectRef.current?.open()}
          />
          <IonSelect
            ref={competitionSelectRef}
            aria-label="Competición"
            interface="popover"
            value={selectedSeasonCompetitionId}
            disabled={seasonCompetitionsLoading}
            onIonChange={(event) => selectSeasonCompetition(event.detail.value as string | undefined)}
            className="pointer-events-none absolute inset-0 opacity-0"
          >
            {seasonCompetitions.map((seasonCompetition) => (
              <IonSelectOption key={seasonCompetition.id} value={seasonCompetition.id}>
                {formatCompetitionLabel(seasonCompetition.competition)}
              </IonSelectOption>
            ))}
          </IonSelect>
        </div>
      </div>

      {/* Match week navigator */}
      {matchWeekMatches && (
        <div className="mx-4 mt-3 flex items-center justify-between rounded-2xl border border-[#e8ebf1] bg-white px-2.5 py-2 shadow-[0_1px_2px_rgba(20,30,60,0.04)]">
          <button
            type="button"
            disabled={!matchWeekMatches.hasPrevious}
            onClick={goToPreviousMatchWeek}
            aria-label="Jornada anterior"
            className="flex h-9 w-9 items-center justify-center rounded-xl border border-[#e2e7ef] bg-white text-[var(--fc-navy)] transition-colors hover:bg-slate-50 disabled:opacity-40"
          >
            <IonIcon icon={chevronBack} className="text-xl" />
          </button>

          <div className="font-heading text-[17px] font-extrabold text-[var(--fc-ink)]">
            Jornada {matchWeekMatches.currentMatchWeek.weekNumber}
          </div>

          <button
            type="button"
            disabled={!matchWeekMatches.hasNext}
            onClick={goToNextMatchWeek}
            aria-label="Jornada siguiente"
            className="flex h-9 w-9 items-center justify-center rounded-xl border border-[#e2e7ef] bg-white text-[var(--fc-navy)] transition-colors hover:bg-slate-50 disabled:opacity-40"
          >
            <IonIcon icon={chevronForward} className="text-xl" />
          </button>
        </div>
      )}
    </>
  );
};
